import moment from "moment-timezone";

const RELATIVE_TIME_THRESHOLD_HOURS = 3;

// Configure moment's format strings
const DATE_FORMATS = {
  timeOnly: (includeSeconds: boolean) => (includeSeconds ? "h:mm:ss A" : "h:mm A"),
  full: (includeSeconds: boolean) =>
    includeSeconds ? "MMM D, YYYY [at] h:mm:ss A" : "MMM D, YYYY [at] h:mm A",
};

export function formatTimestamp(
  timestamp: string | Date | moment.Moment,
  options: {
    showRelativeWithinHours?: number;
    showRelative?: boolean;
    includeSeconds?: boolean;
    format?: "full" | "time-only";
  } = {},
): string {
  const {
    showRelativeWithinHours = RELATIVE_TIME_THRESHOLD_HOURS,
    showRelative = true,
    includeSeconds = false,
    format = "full",
  } = options;

  const time = moment(timestamp);

  if (!time.isValid()) {
    return "Invalid date";
  }

  // Show relative time if within threshold and format is "full"
  if (
    showRelative &&
    format === "full" &&
    Math.abs(moment().diff(time, "hours", true)) <= showRelativeWithinHours
  ) {
    return time.fromNow();
  }

  // Return formatted timestamp based on format type
  switch (format) {
    case "time-only":
      return time.format(DATE_FORMATS.timeOnly(includeSeconds));
    default:
      return time.format(DATE_FORMATS.full(includeSeconds));
  }
}

export function formatTimestampUTC(
  timestamp: string | Date | moment.Moment,
  options: Parameters<typeof formatTimestamp>[1] = {},
): string {
  return formatTimestamp(moment.utc(timestamp).local(), options);
}

/**
 * Format a UTC timestamp in the given IANA timezone (e.g. "America/New_York", "UTC").
 * Pass "local" or undefined to use the browser's local timezone.
 * Uses moment-timezone for accurate DST handling.
 */
export function formatTimestampInZone(
  timestamp: string | Date | moment.Moment,
  timezone: string | undefined,
  options: Parameters<typeof formatTimestamp>[1] = {},
): string {
  if (!timezone || timezone === "local") {
    return formatTimestampUTC(timestamp, options);
  }

  const {
    showRelativeWithinHours = RELATIVE_TIME_THRESHOLD_HOURS,
    showRelative = true,
    includeSeconds = false,
    format = "full",
  } = options;

  const time = moment.utc(timestamp).tz(timezone);

  if (!time.isValid()) {
    return "Invalid date";
  }

  if (
    showRelative &&
    format === "full" &&
    Math.abs(moment().diff(time, "hours", true)) <= showRelativeWithinHours
  ) {
    return time.fromNow();
  }

  switch (format) {
    case "time-only":
      return time.format(DATE_FORMATS.timeOnly(includeSeconds));
    default:
      return time.format(DATE_FORMATS.full(includeSeconds));
  }
}
export function formatDateRange(from: Date | moment.Moment, to: Date | moment.Moment): string {
  const fromMoment = moment(from);
  const toMoment = moment(to);
  const dateOnlyFormat = "MMM D, YYYY";
  const dateTimeFormat = "MMM D, YYYY [at] h:mm A";

  if (!fromMoment.isValid() || !toMoment.isValid()) {
    return "Invalid date range";
  }

  // Same day - show single date with time range
  if (fromMoment.isSame(toMoment, "day")) {
    return `${fromMoment.format(dateOnlyFormat)} ${fromMoment.format(
      "h:mm A",
    )} - ${toMoment.format("h:mm A")}`;
  }

  // Same year - omit year from start date
  if (fromMoment.isSame(toMoment, "year")) {
    return `${fromMoment.format("MMM D [at] h:mm A")} - ${toMoment.format(dateTimeFormat)}`;
  }

  // Different years - show full dates with times
  return `${fromMoment.format(dateTimeFormat)} - ${toMoment.format(dateTimeFormat)}`;
}

/** Compact relative time like "now", "13m", "5h", "3d", "2w", "4mo", "1y". */
const MINUTES_PER_HOUR = moment.duration(1, "hour").asMinutes();
const HOURS_PER_DAY = moment.duration(1, "day").asHours();
const DAYS_PER_WEEK = moment.duration(1, "week").asDays();
const MONTHS_PER_YEAR = moment.duration(1, "year").asMonths();

export const formatCompactRelativeTime = (timestamp: string | Date | moment.Moment): string => {
  const time = moment.utc(timestamp).local();
  if (!time.isValid()) {
    return "";
  }

  const elapsed = moment.duration(Math.max(0, moment().diff(time)));
  if (elapsed.asMinutes() < 1) {
    return "now";
  }
  const minutes = Math.floor(elapsed.asMinutes());
  if (minutes < MINUTES_PER_HOUR) {
    return `${minutes}m`;
  }
  const hours = Math.floor(elapsed.asHours());
  if (hours < HOURS_PER_DAY) {
    return `${hours}h`;
  }
  const days = Math.floor(elapsed.asDays());
  if (days < DAYS_PER_WEEK) {
    return `${days}d`;
  }
  const months = Math.floor(elapsed.asMonths());
  if (months < 1) {
    return `${Math.floor(elapsed.asWeeks())}w`;
  }
  if (months < MONTHS_PER_YEAR) {
    return `${months}mo`;
  }
  return `${Math.floor(elapsed.asYears())}y`;
};

export { RELATIVE_TIME_THRESHOLD_HOURS };

/** Inbox-style short timestamp: "3:04 PM" today, "Oct 30, 3:04 PM" this year, else "Oct 30, 2025". */
export const formatShortTimestamp = (timestamp: string | Date | moment.Moment): string => {
  const time = moment.utc(timestamp).local();
  if (!time.isValid()) {
    return "";
  }
  const now = moment();
  if (time.isSame(now, "day")) {
    return time.format("h:mm A");
  }
  if (time.isSame(now, "year")) {
    return time.format("MMM D, h:mm A");
  }
  return time.format("MMM D, YYYY");
};
